import React from 'react';

export interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * `brand` is the default. Use `current` inside a coloured surface — a primary
   * button's saving state — where the spinner should inherit the text colour
   * instead of turning brand purple on purple.
   */
  tone?: 'brand' | 'current';
  label?: string;
  className?: string;
}

/**
 * The size ramp is derived from what the app actually used before P2-04 replaced
 * 61 hand-rolled spinners: h-4 ×11, h-5 ×2, h-6 ×7, h-7 ×2, h-8 ×30, h-12 ×8.
 * Every common size lands exactly or within 4px.
 *
 * Border width scales with diameter. It previously did not, so `xl` was a 128px
 * circle drawn with a 2px arc — a hairline ring rather than a spinner, and it was
 * on the app's main loading screen. `xl` is now 48px, which is as large as this
 * ever needs to be.
 */
const sizeMap = {
  sm: 'h-4 w-4 border-b-2',
  md: 'h-6 w-6 border-b-2',
  lg: 'h-8 w-8 border-b-[3px]',
  xl: 'h-12 w-12 border-b-4',
};

const toneMap = {
  brand: 'border-primary-600',
  current: 'border-current',
};

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  tone = 'brand',
  label = 'Loading',
  className = '',
}) => {
  return (
    <div
      className={`animate-spin rounded-full ${sizeMap[size]} ${toneMap[tone]} ${className}`}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <span className="sr-only">{label}</span>
    </div>
  );
};

export default LoadingSpinner;
